import React, { useEffect, useState, useRef, useCallback } from 'react';
import Helmet from 'react-helmet';
import cn from 'classnames';
// Hooks
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import useAPI from 'src/hooks/useAPI';
// Paths
import {
  push,
  directBookingTechSkusPath,
  directBookingTechProductsPath,
  homePath,
} from 'src/utils/paths';
// Images
import techClientsMobileJpg from 'src/images/serviceImages/tech-client-speaker-mobile.jpg';
import techClientsMobileWebp from 'src/images/serviceImages/tech-client-speaker-mobile.webp';
import techClientsTabletJpg from 'src/images/serviceImages/tech-client-speaker-tablet.jpg';
import techClientsTabletWebp from 'src/images/serviceImages/tech-client-speaker-tablet.webp';
import techClientsDesktopJpg from 'src/images/serviceImages/tech-client-speaker-desktop.jpg';
import techClientsDesktopWebp from 'src/images/serviceImages/tech-client-speaker-desktop.webp';
// Components && Styles && Others
import { logger } from 'src/utils/logger';
import { layoutUpdate } from 'src/actions/common';
import { loadProfileData } from 'src/components/Techs/data/actions';
import useNotice from 'src/hooks/useNotice';
import { techProfileByIdJSSelector } from 'src/selectors/techs';
import { getFullFirstLastInitial } from 'src/utils/text';
import { HIDE_PRODUCT_CONNECTED_HOME } from 'src/constants/app';
import { BUTTON_CTAS } from 'src/containers/CartPage/constants';
import PageLoader from 'src/components/PageLoader';
import Button, { THEMES } from 'src/components/HTKit/Forms/Button';
import SubNav from 'src/components/Layout/SubNav';
import { TYPES as PICTURE_TYPES } from 'src/components/Elements/Picture';
import ButtonFooter from 'src/components/ButtonFooter';
import { Grid, Breakpoints } from 'HTKit/Grid';
import { CustomerReviewsStacked } from 'src/components/Elements/CustomerReviews';
import GuaranteeBlock from 'src/components/Sections/GuaranteeBlock';
import {
  TechProfileHero,
  BookingPathSelector,
  FloatingHeaderContent,
} from 'src/containers/DirectBooking/TechProfilePage/Parts';
import { DIRECT_BOOKING_LAYOUT, DIRECT_BOOKING_HEADER_TRIGGER_ID } from '../constants';
import { fillDirectBookingCookie } from '../utils';
import styles from './styles.scss';

const getGuaranteeImageSources = () => {
  const showMdGrid = `(min-width: ${Breakpoints.MD_GRID}px)`;
  const showLgGrid = `(min-width: ${Breakpoints.LG_GRID}px)`;
  return [
    { srcSet: techClientsDesktopWebp, type: PICTURE_TYPES.WEBP, media: showLgGrid },
    { srcSet: techClientsDesktopJpg, type: PICTURE_TYPES.JPG, media: showLgGrid },
    { srcSet: techClientsTabletWebp, type: PICTURE_TYPES.WEBP, media: showMdGrid },
    { srcSet: techClientsTabletJpg, type: PICTURE_TYPES.JPG, media: showMdGrid },
    { srcSet: techClientsMobileWebp, type: PICTURE_TYPES.WEBP },
    { srcSet: techClientsMobileJpg, type: PICTURE_TYPES.JPG },
  ];
};

const TechProfilePage = () => {
  const params = useParams();
  const [tech, setTech] = useState({ id: null });
  const { techId } = params;
  const dispatch = useDispatch();
  const api = useAPI();
  const { error: errorSnackbar, requestError: requestErrorSnackbar } = useNotice();
  const [pageLoaded, setPageLoaded] = useState(false);
  const techProfile = useSelector(techProfileByIdJSSelector(tech.id));

  const { dedicatedPhone, firstName = '', lastName = '', ratings = [], slug: techSlug = '' } =
    techProfile || {};
  const techName = getFullFirstLastInitial(firstName, lastName);

  // Load Tech Data
  useEffect(() => {
    (async () => {
      dispatch(layoutUpdate(DIRECT_BOOKING_LAYOUT));
      try {
        if (techProfile) {
          setPageLoaded(true);
          return;
        }

        const requestResult = await api.techs.profile({ id: techId });

        if (!requestResult.err) {
          const {
            data: { tech: techData },
          } = requestResult;
          setTech(techData);
          dispatch(loadProfileData({ techId: techData.id, data: techData }));
          setPageLoaded(true);
        } else {
          const {
            data: { errors },
          } = requestResult;
          dispatch(push(homePath()));
          requestErrorSnackbar(requestResult);
          logger('TechSkusPage - response data')(errors);
        }
      } catch (e) {
        errorSnackbar("This technician's profile could not load. Please contact support.");
        logger('TechSkusPage')(e);
      }
    })();
  }, [techId]);

  const guaranteeBlockImageSources = getGuaranteeImageSources();

  const bookNow = () => {
    fillDirectBookingCookie({ techId, techPhoneNumber: dedicatedPhone, techSlug });
    dispatch(push(directBookingTechSkusPath(techId)));
  };

  const handleShopProductsClick = () => {
    fillDirectBookingCookie({ techId, techPhoneNumber: dedicatedPhone, techSlug });
    dispatch(push(directBookingTechProductsPath(techId)));
  };

  const BookNowButton = (props) => (
    <Button mediumV2 theme={THEMES.V2PRIMARY} onClick={bookNow} {...props}>
      Book A Service
    </Button>
  );

  const reviewsRef = useRef(null);

  const handleRatingClick = useCallback(() => {
    window.scrollTo({
      top: reviewsRef.current.offsetTop,
    });
  }, []);

  const showProductsButton = !HIDE_PRODUCT_CONNECTED_HOME;

  if (!pageLoaded) return <PageLoader />;

  const helmetTitle = techName ? `Meet Technician ${techName}` : '';

  return (
    <div>
      <Helmet title={helmetTitle} />
      <SubNav
        startElementSelector={`#${DIRECT_BOOKING_HEADER_TRIGGER_ID}`}
        showDesktopOnly
        innerClasses={styles.floatingNavWrapper}
      >
        <FloatingHeaderContent
          techInfo={techProfile}
          techName={techName}
          onProductClick={handleShopProductsClick}
          onServiceClick={bookNow}
        />
      </SubNav>
      <div className={styles.heroContainer}>
        <TechProfileHero
          bookNow={bookNow}
          techInfo={techProfile}
          techName={techName}
          onRatingClick={handleRatingClick}
        />
      </div>
      <Grid.Fluid classes="paddingBottom-medium2">
        <BookingPathSelector onServiceClick={bookNow} onProductClick={handleShopProductsClick} />
      </Grid.Fluid>
      <div id={DIRECT_BOOKING_HEADER_TRIGGER_ID} />
      <Grid.Fluid classes={styles.reviews}>
        <CustomerReviewsStacked ref={reviewsRef} headerText="Recent Reviews" reviews={ratings} />
      </Grid.Fluid>

      <GuaranteeBlock
        classes={styles.guaranteeBlockContainer}
        imageSources={guaranteeBlockImageSources}
        ctaButton={
          <BookNowButton className={cn('marginTop-small1', styles.button, styles.guaranteeCta)} />
        }
      />

      <ButtonFooter showDesktop={false}>
        <BookNowButton />
        {showProductsButton && (
          <Button
            mediumV2
            theme={THEMES.V2SECONDARY}
            className="marginTop-small"
            onClick={handleShopProductsClick}
          >
            {BUTTON_CTAS.PRODUCTS}
          </Button>
        )}
      </ButtonFooter>
    </div>
  );
};

export default TechProfilePage;
